import styled from '@emotion/styled';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';

import Alert from '~/common/components/Alerts/Alert';
import Box from '~/common/components/Box';
import FormLabel from '~/common/components/FormLabel';
import { createRequiredValidator } from '~/common/form/utils';
import ToggleButtonGroup from '~/common/mui/Buttons/MUIButtonGroup/ToggleButtonGroup';
import Checkbox from '~/common/mui/Checkboxes/Checkbox/MuiCheckbox';
import Switch from '~/common/mui/Switch/MuiSwitch/Switch';
import ScrollIntoViewContainer from '~/pages/Loan1003DataGrids/common/components/ScrollIntoViewContainer';
import palette from '~/theme/mui/palette';

import type { RunAUSRightColumnExtraProps } from './AUSType';
import {
  type AUSFieldValue,
  type AUSFormData,
  AUS_FORM_MESSAGES,
  withForm,
} from './hooks/ausFormHook';

const StyledLegendText = styled<any>('legend')`
  font-size: 1rem;
  font-weight: 300;
  text-transform: uppercase;
  color: ${palette.black};
  opacity: ${(props) => (props.disabled ? 0.4 : 1)};
`;

const StyledLegendText2 = styled<any>('legend')`
  font-size: 1rem;
  font-weight: 500;
  color: ${palette.black};
  opacity: ${(props) => (props.disabled ? 0.4 : 1)};
`;

function validateAgency(
  value: AUSFieldValue,
  creditOption: string,
): string | undefined {
  if (creditOption !== 'reissue') return undefined;
  return value && value !== 'Select one' && value !== ''
    ? undefined
    : AUS_FORM_MESSAGES.agencyRequired;
}

function validateRequiredWhenReissue(
  value: AUSFieldValue,
  creditOption: string,
  message: string,
): string | undefined {
  if (creditOption !== 'reissue') return undefined;
  return value ? undefined : message;
}

function validateRequiredWhenNotReissue(
  value: AUSFieldValue,
  creditOption: string,
  message: string,
): string | undefined {
  if (creditOption === 'reissue') return undefined;
  return value ? undefined : message;
}

function validateLpaBranchNumber(value: AUSFieldValue): string | undefined {
  if (!value) return undefined;
  return String(value).length === 4
    ? undefined
    : AUS_FORM_MESSAGES.lpaBranchLength;
}

const RightColumn = withForm({
  defaultValues: {} as AUSFormData,
  props: {} as RunAUSRightColumnExtraProps,
  render: function RightColumn({
    form,
    creditOption,
    setCreditOption,
    selectedAusOption,
    setSelectedAusOption,
    formDisabled,
    lastRunAUS,
    agencyOptions,
    getInitialAgency,
    useBrokerCredentials,
    setUseBrokerCredentials,
    isUseBrokerCredentialsDisabled,
    freOfferingOptions,
    borrowerCreditReferences,
    lpaLoanAssociationDetails,
    loanAssociationDetails,
    positiveRentalPaymentText,
    updatedPositiveRentalPaymentIndicator,
    hasOneClickAUS,
    loanProductType,
    firstErrorSection,
    errorScrollTrigger,
  }) {
    const loanProduct = loanProductType;

    const showPositiveRentalPaymentHistory =
      ((loanProduct === 'Conventional' && selectedAusOption === 'LPA') ||
        loanProduct === 'FHA') &&
      positiveRentalPaymentText !== null &&
      positiveRentalPaymentText !== undefined;

    const handleAusOptionChange = (value: string) => {
      setSelectedAusOption(value as 'DU' | 'LPA');

      const newAgency = getInitialAgency(
        value,
        lpaLoanAssociationDetails,
        loanAssociationDetails,
      );
      form.setFieldValue('agency', newAgency ?? '');

      if (value === 'DU') {
        const brokerCredentialsInd =
          loanAssociationDetails.brokerCredentialsInd ?? false;
        setUseBrokerCredentials(brokerCredentialsInd);
        if (brokerCredentialsInd) {
          form.setFieldValue('userName', loanAssociationDetails.username ?? '');
          form.setFieldValue('password', loanAssociationDetails.password ?? '');
        }
      } else {
        setUseBrokerCredentials(false);
      }
    };

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '500px',
        }}
      >
        {/* Row 1: Button Groups */}
        <Box sx={{ display: 'flex', marginBottom: '1.5rem', gap: 4 }}>
          <ToggleButtonGroup
            options={[
              { value: 'existing', label: 'Use Existing Credit' },
              { value: 'reissue', label: 'Re-Issue' },
            ]}
            value={creditOption}
            onChange={setCreditOption}
            size="medium"
          />
          <ToggleButtonGroup
            options={[
              { value: 'DU', label: 'DU' },
              { value: 'LPA', label: 'LPA' },
            ]}
            value={selectedAusOption}
            onChange={handleAusOptionChange}
            size="medium"
            disabled={formDisabled && !hasOneClickAUS}
          />
        </Box>

        {lastRunAUS !== selectedAusOption && (
          <Box sx={{ marginBottom: '1.5rem' }}>
            <Alert status="warning" isNotice>
              Last AUS run was {lastRunAUS}
            </Alert>
          </Box>
        )}

        {/* Row 2: Agency Select */}
        <ScrollIntoViewContainer
          valueToWatch={
            firstErrorSection === 'agency' ? errorScrollTrigger : undefined
          }
        >
          <form.AppField
            name="agency"
            validators={{
              onSubmit: ({ value }: { value: AUSFieldValue }) =>
                validateAgency(value, creditOption),
            }}
          >
            {(field) => (
              <field.Select
                id="agency"
                label="Agency"
                isDisabled={formDisabled}
                sx={{
                  marginBottom: '1.5rem',
                  flex: '1 0 0',
                  backgroundColor: 'white',
                  width: '100%',
                }}
              >
                {agencyOptions.map((opt) => (
                  <MenuItem key={opt.key} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </field.Select>
            )}
          </form.AppField>
        </ScrollIntoViewContainer>

        {/* Row 3: DU Credentials */}
        {selectedAusOption === 'DU' && (
          <ScrollIntoViewContainer
            valueToWatch={
              firstErrorSection === 'credentials'
                ? errorScrollTrigger
                : undefined
            }
          >
            <Box component="fieldset">
              <StyledLegendText disabled={formDisabled}>
                Credentials
              </StyledLegendText>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <form.AppField
                  name="userName"
                  validators={{
                    onSubmit: ({ value }: { value: AUSFieldValue }) =>
                      validateRequiredWhenReissue(
                        value,
                        creditOption,
                        AUS_FORM_MESSAGES.userNameRequired,
                      ),
                  }}
                >
                  {(field) => (
                    <field.TextField
                      sx={{
                        flex: 1,
                        '& .MuiInputBase-root': {
                          backgroundColor: palette?.white,
                        },
                      }}
                      id="userName"
                      label="User Name"
                      placeholder="Enter User Name"
                      isDisabled={formDisabled || useBrokerCredentials}
                    />
                  )}
                </form.AppField>

                <form.AppField
                  name="password"
                  validators={{
                    onSubmit: ({ value }: { value: AUSFieldValue }) =>
                      validateRequiredWhenReissue(
                        value,
                        creditOption,
                        AUS_FORM_MESSAGES.passwordRequired,
                      ),
                  }}
                >
                  {(field) => (
                    <field.PasswordInput
                      sx={{ flex: 1 }}
                      id="password"
                      label="Password"
                      disabled={formDisabled || useBrokerCredentials}
                    />
                  )}
                </form.AppField>
              </Box>

              <Box
                component="fieldset"
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                }}
              >
                <FormLabel
                  label="Use Broker Credentials"
                  htmlFor="useBrokerCredentials"
                  sx={{ marginBottom: 'unset', fontWeight: 400 }}
                  disabled={formDisabled || isUseBrokerCredentialsDisabled}
                />
                <Switch
                  name="useBrokerCredentials"
                  disabled={formDisabled || isUseBrokerCredentialsDisabled}
                  onChange={(value) => setUseBrokerCredentials(value)}
                  checked={useBrokerCredentials}
                />
              </Box>
            </Box>
          </ScrollIntoViewContainer>
        )}

        {selectedAusOption === 'LPA' && (
          <ScrollIntoViewContainer
            valueToWatch={
              firstErrorSection === 'lpa' ? errorScrollTrigger : undefined
            }
          >
            <Box component="fieldset" sx={{ marginBottom: '1.5rem' }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <form.AppField
                  name="freOfferingId"
                  validators={{
                    onSubmit: ({ value }: { value: AUSFieldValue }) =>
                      validateRequiredWhenNotReissue(
                        value,
                        creditOption,
                        AUS_FORM_MESSAGES.freOfferingRequired,
                      ),
                  }}
                >
                  {(field) => (
                    <field.Select
                      id="freOfferingId"
                      label="FRE Offering ID"
                      isDisabled={false}
                      sx={{ flexBasis: '80%', width: '100%' }}
                    >
                      {freOfferingOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </field.Select>
                  )}
                </form.AppField>

                <form.AppField
                  name="lpaBranchNumber"
                  validators={{
                    onSubmit: ({ value }: { value: AUSFieldValue }) =>
                      validateLpaBranchNumber(value),
                  }}
                >
                  {(field) => (
                    <field.TextField
                      sx={{
                        flexBasis: '20%',
                        width: '100%',
                        '& .MuiInputBase-root': {
                          backgroundColor: palette?.white,
                        },
                      }}
                      id="lpaBranchNumber"
                      label="LPA Branch #"
                      placeholder="Enter LPA Branch #"
                      disabled={formDisabled}
                    />
                  )}
                </form.AppField>
              </Box>
            </Box>
          </ScrollIntoViewContainer>
        )}

        {/* Row 4: Credit Ref Numbers */}
        <ScrollIntoViewContainer
          valueToWatch={
            firstErrorSection === 'creditRefs' ? errorScrollTrigger : undefined
          }
        >
          <Box
            component="fieldset"
            sx={{
              marginBottom: '1.5rem',
              width: '100%',
              boxSizing: 'border-box',
            }}
          >
            <StyledLegendText disabled={formDisabled}>
              Credit Ref Numbers
            </StyledLegendText>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '0.5rem',
                width: '100%',
              }}
            >
              {borrowerCreditReferences.map((borrower) => {
                const fieldName = `ref_${borrower.borrowerIdentifier?.customerRecordId}`;
                return (
                  <Box
                    key={fieldName}
                    sx={{
                      flex: { xs: '1 1 100%', sm: '1 1 48%' },
                      maxWidth: { sm: 'calc(50% - 0.25rem)' },
                      minWidth: 0,
                    }}
                  >
                    <form.AppField
                      name={fieldName}
                      validators={{
                        onSubmit: createRequiredValidator(
                          AUS_FORM_MESSAGES.creditRefRequired,
                        ),
                      }}
                    >
                      {(field) => (
                        <field.TextField
                          sx={{
                            width: '100%',
                            '& .MuiInputBase-root': {
                              backgroundColor: palette?.white,
                            },
                          }}
                          id={fieldName}
                          label={borrower.fullName ?? 'Reference'}
                          placeholder="Enter Reference"
                          disabled={formDisabled}
                        />
                      )}
                    </form.AppField>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </ScrollIntoViewContainer>

        {/* Row 5: Positive Rental Payment History */}
        {showPositiveRentalPaymentHistory && (
          <Box component="fieldset" sx={{ marginBottom: '1.5rem' }}>
            <Box sx={{ display: 'flex' }}>
              <FormControl>
                <StyledLegendText2>
                  Positive Rental Payment History
                </StyledLegendText2>
                <form.AppField name="positiveRentalPaymentHistory">
                  {(field) => (
                    <FormControlLabel
                      data-testid="positive-rental-payment-label"
                      sx={{ marginBottom: '1.5rem' }}
                      control={
                        <Checkbox
                          checked={!!(field.state.value as boolean)}
                          onChange={(e) => {
                            const isChecked = e.target.checked;
                            field.handleChange(isChecked);
                            updatedPositiveRentalPaymentIndicator(isChecked);
                          }}
                        />
                      }
                      label={positiveRentalPaymentText}
                    />
                  )}
                </form.AppField>
              </FormControl>
            </Box>
          </Box>
        )}
      </Box>
    );
  },
});

export default RightColumn;
